import { useMemo } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { StatsBar } from './StatsBar';
import { LiveOnSitePanel } from './LiveOnSitePanel';
import { ComplianceSummaryCard } from './ComplianceSummaryCard';
import { GeofenceIndicator } from './GeofenceIndicator';
import { RecentActivityFeed } from './RecentActivityFeed';

export default function DashboardView({
  onSiteUsers,
  recentCheckIns,
  activityFeed,
  stats,
  gapAnalysis,
  gapAnalysisCounts,
  siteLocation,
  getEmployeeStats,
  statsLoading,
  onCheckOut,
  onViewSelfie,
  onViewCompliance,
  alertSettings,
  loading,
}) {
  const shortageCount = gapAnalysisCounts?.shortageCount || 0;
  const threshold = alertSettings?.critical_shortage_threshold || 2;

  const showCriticalAlert = useMemo(
    () => shortageCount >= threshold,
    [shortageCount, threshold]
  );

  if (loading) {
    return (
      <div className="flex flex-col h-full overflow-hidden bg-gray-50/50">
        {/* Skeleton Stats Bar */}
        <div className="px-6 pt-4 pb-2">
          <div className="flex gap-3">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-3 bg-white border rounded-xl flex-1"
              >
                <Skeleton className="h-8 w-8 rounded-lg" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3 w-12" />
                  <Skeleton className="h-5 w-8" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Skeleton Main Content */}
        <div className="flex-1 min-h-0 flex gap-4 px-6 pb-4 pt-2">
          {/* Left column skeleton */}
          <div className="flex-[2] min-w-0 flex flex-col">
            <div className="bg-white border rounded-xl flex-1 p-4 space-y-3">
              <div className="flex items-center justify-between mb-4">
                <Skeleton className="h-5 w-36" />
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 border rounded-lg"
                >
                  <Skeleton className="h-10 w-10 rounded-xl" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-8 w-20 rounded-md" />
                </div>
              ))}
            </div>
          </div>

          {/* Right column skeleton */}
          <div className="flex-[1] min-w-0 flex flex-col gap-4 max-w-sm">
            {/* Compliance card skeleton */}
            <div className="bg-white border rounded-xl p-4 space-y-3">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-2 w-full rounded-full" />
              <div className="flex gap-2">
                <Skeleton className="h-16 flex-1 rounded-lg" />
                <Skeleton className="h-16 flex-1 rounded-lg" />
              </div>
            </div>

            {/* Geofence skeleton */}
            <div className="bg-white border rounded-xl p-4 space-y-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-24" />
            </div>

            {/* Activity feed skeleton */}
            <div className="bg-white border rounded-xl p-4 flex-1 space-y-3">
              <Skeleton className="h-5 w-32" />
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Skeleton className="h-6 w-6 rounded-full" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-3 w-28" />
                    <Skeleton className="h-2.5 w-20" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50/50">
      {/* Critical Alert Banner - compact, conditional */}
      {showCriticalAlert && (
        <div className="px-6 pt-4">
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5"
          >
            <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
            <p className="text-sm text-red-800 flex-1">
              <span className="font-semibold">
                {shortageCount} compliance gap{shortageCount !== 1 ? 's' : ''}
              </span>{' '}
              require{shortageCount === 1 ? 's' : ''} attention
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={onViewCompliance}
              className="h-7 text-xs text-red-700 hover:bg-red-100"
            >
              View Details
            </Button>
          </motion.div>
        </div>
      )}

      {/* Stats Bar */}
      <StatsBar
        onSiteCount={stats?.onSiteNow || onSiteUsers?.length || 0}
        checkIns={recentCheckIns}
        gapAnalysis={gapAnalysis}
        gapAnalysisCounts={gapAnalysisCounts}
      />

      {/* Main Content - flex row */}
      <div className="flex-1 min-h-0 flex gap-4 px-6 pb-4">
        {/* Left column: Live On-Site (~65%) */}
        <div className="flex-[2] min-w-0 flex flex-col">
          <LiveOnSitePanel
            onSiteUsers={onSiteUsers}
            getEmployeeStats={getEmployeeStats}
            statsLoading={statsLoading}
            onCheckOut={onCheckOut}
            onViewSelfie={onViewSelfie}
          />
        </div>

        {/* Right column: Widgets (~35%) */}
        <div className="flex-[1] min-w-0 flex flex-col gap-4 max-w-sm">
          <ComplianceSummaryCard
            gapAnalysis={gapAnalysis}
            gapAnalysisCounts={gapAnalysisCounts}
            onViewDetails={onViewCompliance}
          />
          <GeofenceIndicator siteLocation={siteLocation} />
          <RecentActivityFeed
            activityFeed={activityFeed}
            onViewSelfie={onViewSelfie}
          />
        </div>
      </div>
    </div>
  );
}
